import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import {
  Monitor,
  Users,
  Target,
  DollarSign,
  ArrowRight,
  Sparkles,
  Zap,
  ChevronDown,
  CheckCircle
} from "lucide-react";
import RelatedLinks from "@/components/ui/related-links";
import { motion } from "framer-motion";
import { AuroraHero } from "@/components/ui/aurora-hero";

const Services = () => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const handleCardExpand = (index: number) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  const services = [
    {
      icon: Monitor,
      title: "Serviços de TI",
      slug: "ti",
      description: "Soluções completas de tecnologia com foco em desenvolvimento e outsourcing Fluig.",
      expandedDescription: "Nossa área de TI oferece soluções completas desde outsourcing Fluig até desenvolvimento customizado com as mais modernas tecnologias. Especialistas em Javascript, React, Python e nossa exclusiva FluigDESK.",
      badge: "Tecnologia",
      features: [
        "Outsourcing Fluig Especializado",
        "Desenvolvimento Javascript/React",
        "Soluções Python Avançadas",
        "FluigDESK - Versão Future Station"
      ],
      expandedFeatures: [
        "Outsourcing Fluig com equipe dedicada",
        "Desenvolvimento frontend React/TypeScript",
        "Backend Python com APIs REST",
        "FluigDESK - Nossa versão customizada",
        "Integrações complexas e microserviços",
        "DevOps e automatização de deploy"
      ],
      benefits: [
        "Equipe especializada em Fluig",
        "Tecnologias modernas e escaláveis",
        "Suporte técnico 24/7 incluído"
      ],
      color: "from-fs-purple-500 to-fs-cyan-500",
      hoverColor: "hover:shadow-fs-hover hover:border-fs-purple-500/50"
    },
    {
      icon: Users,
      title: "Recursos Humanos",
      slug: "rh",
      description: "Automatização completa dos processos de RH com agilidade e eficiência digital.",
      expandedDescription: "Transforme seus processos de Recursos Humanos com nossa solução completa de automação. Desde admissão digital até gestão de férias e desligamentos, tudo integrado com sua folha de pagamento.",
      badge: "Pessoas",
      features: [
        "Processos Humanos Ágeis",
        "Admissão Digital Completa",
        "Gestão de Desligamentos",
        "Controle de Férias e Abonos"
      ],
      expandedFeatures: [
        "Onboarding digital automatizado",
        "Admissão com coleta eletrônica de documentos",
        "Workflows de aprovação de férias",
        "Gestão de abonos e folgas",
        "Desligamento com checklist digital",
        "Integração nativa com folha de pagamento"
      ],
      benefits: [
        "Redução de 80% no tempo de admissão",
        "Processos 100% paperless",
        "Integração direta com folha"
      ],
      color: "from-fs-cyan-500 to-fs-magenta-500",
      hoverColor: "hover:shadow-fs-hover hover:border-fs-cyan-500/50"
    },
    {
      icon: Target,
      title: "Gestão da Qualidade",
      slug: "qualidade",
      description: "Controle total da qualidade com gestão de não conformidades e documentos.",
      expandedDescription: "Sistema completo de gestão da qualidade que permite controle rigoroso de não conformidades, acesso facilitado a documentos e acompanhamento detalhado dos processos produtivos.",
      badge: "Qualidade",
      features: [
        "Controle de Não Conformidades",
        "Gestão de Documentos",
        "Acompanhamento da Produção",
        "Processos de Qualidade"
      ],
      expandedFeatures: [
        "Registro e tratamento de não conformidades",
        "Biblioteca digital de documentos",
        "Controle de versões e aprovações",
        "Indicadores de qualidade em tempo real",
        "Auditorias e inspeções digitais",
        "Ações corretivas e preventivas"
      ],
      benefits: [
        "Conformidade com ISO 9001",
        "Redução de não conformidades",
        "Rastreabilidade completa"
      ],
      color: "from-fs-magenta-500 to-fs-purple-500",
      hoverColor: "hover:shadow-fs-hover hover:border-fs-magenta-500/50"
    },
    {
      icon: DollarSign,
      title: "Processos Financeiros",
      slug: "financeiro",
      description: "Automatização completa de processos financeiros e gestão de fornecedores.",
      expandedDescription: "Solução completa para automatizar todos os processos financeiros, desde solicitação de compras até reembolso de despesas, com gestão integrada de fornecedores e fluxos automatizados.",
      badge: "Financeiro",
      features: [
        "Solicitação de Compras",
        "Cadastro de Fornecedores",
        "Reembolso de Despesas",
        "Fluxos Financeiros Automatizados"
      ],
      expandedFeatures: [
        "Portal de solicitação de compras",
        "Autosserviço para cadastro de fornecedores",
        "Gestão completa de reembolsos",
        "Aprovações financeiras automáticas",
        "Integração com sistemas contábeis",
        "Relatórios financeiros em tempo real"
      ],
      benefits: [
        "Redução de 70% no tempo de aprovação",
        "Controle total de gastos",
        "Integração com ERP existente"
      ],
      color: "from-fs-purple-500 to-fs-magenta-500",
      hoverColor: "hover:shadow-fs-hover hover:border-fs-purple-500/50"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Serviços Especializados em Fluig e TOTVS"
        description="Soluções completas por área: TI, RH, Financeiro e Qualidade. Outsourcing Fluig, automação de processos e desenvolvimento customizado com mais de 10 anos de experiência."
        keywords={['serviços fluig', 'outsourcing fluig', 'automação processos', 'totvs fluig', 'consultoria tecnologia', 'desenvolvimento customizado', 'gestão qualidade', 'processos financeiros', 'recursos humanos digital']}
        type="website"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section id="hero">
          <AuroraHero>
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-fs-purple-500/10 backdrop-blur-sm border border-fs-purple-500/30 rounded-full px-6 py-3 mb-8">
                <Sparkles className="w-5 h-5 text-fs-cyan-400" />
                <span className="text-muted-foreground dark:text-white/70 font-medium">
                  Nossos Serviços Especializados
                </span>
              </div>

              {/* Main Title */}
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold font-poppins text-foreground dark:text-white mb-8">
                Soluções por
                <span className="block fs-gradient-text mt-2">
                  Área de Expertise
                </span>
              </h1>

              {/* Description */}
              <p className="text-xl md:text-2xl text-muted-foreground dark:text-white/70 leading-relaxed mb-12 max-w-3xl mx-auto">
                Oferecemos soluções especializadas para cada área da sua empresa,
                com processos otimizados e tecnologia de ponta.
              </p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
                {[
                  { number: "4", label: "Áreas de Expertise" },
                  { number: "50K+", label: "Horas de Experiência" },
                  { number: "100%", label: "Foco em Fluig" },
                  { number: "24/7", label: "Suporte Técnico" }
                ].map((stat, index) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold fs-gradient-text mb-2">
                      {stat.number}
                    </div>
                    <div className="text-sm text-muted-foreground dark:text-white/70">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Button variant="fs-primary" size="lg" className="group">
                  Explorar Soluções
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button variant="fs-outline" size="lg">
                  Falar com Especialista
                </Button>
              </div>
            </div>
          </AuroraHero>
        </section>

        {/* Services Grid Section */}
        <section className="py-24 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-magenta-500/10 rounded-full blur-3xl" />
          </div>

          <Container className="relative z-10">
            <div>
              {/* Section Header */}
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-4xl md:text-5xl font-bold font-poppins text-foreground dark:text-white mb-6">
                  Áreas de
                  <span className="block fs-gradient-text mt-2">
                    Especialização
                  </span>
                </h2>

                <p className="text-xl text-muted-foreground dark:text-white/70 leading-relaxed">
                  Cada área com processos específicos, tecnologias adequadas e 
                  equipe especializada para atender suas necessidades.
                </p>
              </div>

              {/* Services Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {services.map((service, index) => {
                  const IconComponent = service.icon;
                  const isExpanded = expandedCard === index;
                  
                  return (
                    <Card 
                      key={service.title}
                      className={`group bg-card/80 backdrop-blur-sm border border-border hover:-translate-y-2 transition-all duration-500 shadow-md dark:shadow-none ${service.hoverColor} animate-fade-in cursor-pointer ${
                        isExpanded ? 'md:col-span-2' : ''
                      }`}
                      style={{ animationDelay: `${index * 150}ms` }}
                      onClick={() => handleCardExpand(index)}
                    >
                      <CardHeader className="pb-4">
                        <div className="flex items-start justify-between">
                          <div>
                            {/* Icon with gradient background */}
                            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} p-4 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                              <IconComponent className="w-full h-full text-white" />
                            </div>

                            {/* Badge */}
                            <Badge
                              variant="secondary"
                              className="bg-fs-purple-500/20 text-fs-purple-700 dark:text-fs-purple-200 border-fs-purple-500/30 mb-3 w-fit"
                            >
                              {service.badge}
                            </Badge>
                          </div>

                          {/* Expand/Collapse Indicator */}
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-fs-purple-500/20 group-hover:bg-fs-purple-500/30 transition-colors">
                            <ChevronDown 
                              className={`w-4 h-4 text-fs-purple-300 transition-transform duration-300 ${
                                isExpanded ? 'rotate-180' : ''
                              }`} 
                            />
                          </div>
                        </div>

                        {/* Title */}
                        <h3 className="text-2xl font-bold font-poppins text-foreground dark:text-white mb-3 group-hover:fs-gradient-text transition-all duration-300">
                          {service.title}
                        </h3>
                      </CardHeader>

                      <CardContent className="pt-0">
                        {/* Description */}
                        <p className="text-muted-foreground dark:text-white/70 leading-relaxed mb-6">
                          {isExpanded && service.expandedDescription ? service.expandedDescription : service.description}
                        </p>

                        {/* Features List */}
                        <ul className="space-y-3 mb-6">
                          {(isExpanded && service.expandedFeatures ? service.expandedFeatures : service.features).map((feature) => (
                            <li key={feature} className="flex items-center text-muted-foreground dark:text-white/70">
                              <CheckCircle className="w-4 h-4 text-fs-cyan-400 mr-3 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>

                        {/* Benefits Section (only in expanded view) */}
                        {isExpanded && service.benefits && (
                          <div className="mb-6">
                            <h4 className="text-sm font-semibold text-fs-cyan-300 mb-3">Principais Benefícios:</h4>
                            <ul className="space-y-2">
                              {service.benefits.map((benefit) => (
                                <li key={benefit} className="flex items-center text-sm text-muted-foreground dark:text-white/70">
                                  <Zap className="w-3 h-3 text-fs-magenta-400 mr-2 flex-shrink-0" />
                                  {benefit}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* CTA Buttons */}
                        <div className={`${isExpanded ? 'flex space-x-3' : ''}`}>
                          <Link 
                            to={`/servicos/${service.slug}`}
                            className={`${isExpanded ? 'flex-1' : 'w-full'}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button 
                              variant="fs-outline" 
                              size="sm" 
                              className={`${isExpanded ? 'flex-1' : 'w-full'} group-hover:bg-fs-purple-500/20 group-hover:border-fs-purple-500`}
                            >
                              Ver Detalhes
                              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                            </Button>
                          </Link>

                          {isExpanded && (
                            <Button 
                              variant="fs-primary" 
                              size="sm" 
                              className="flex-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Implement contact action
                              }}
                            >
                              <Users className="w-4 h-4 mr-2" />
                              Contratar
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Bottom CTA Section */}
              <div className="mt-24 text-center">
                <div className="bg-gradient-to-r from-fs-purple-500/10 to-fs-cyan-500/10 backdrop-blur-sm border border-fs-purple-500/20 rounded-3xl p-8 max-w-4xl mx-auto">
                  <h3 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                    Precisa de uma solução customizada?
                  </h3>
                  
                  <p className="text-muted-foreground dark:text-white/70 mb-8 max-w-2xl mx-auto text-lg">
                    Nossa equipe está pronta para desenvolver a solução perfeita 
                    para as necessidades específicas da sua empresa.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <Button variant="fs-primary" size="lg">
                      Solicitar Proposta
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    
                    <Button variant="fs-outline" size="lg">
                      Agendar Reunião
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Related Links */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <div className="max-w-6xl mx-auto px-6">
            <RelatedLinks 
              title="Soluções Complementares"
              links={[
                {
                  title: "Consultoria Fluig Especializada",
                  description: "Otimize sua plataforma Fluig com nossa consultoria estratégica e 50.000+ horas de experiência no mercado.",
                  href: "/consultoria-fluig",
                  category: "Consultoria"
                },
                {
                  title: "Consultoria em Inteligência Artificial",
                  description: "Implemente IA nos seus processos com nossa metodologia comprovada e equipe especializada.",
                  href: "/consultoria-ia",
                  category: "Consultoria"
                },
                {
                  title: "Catálogo FluigApps",
                  description: "Acesse nossos 16 FluigApps prontos para acelerar a implementação dos seus processos.",
                  href: "/flows",
                  category: "Soluções"
                },
                {
                  title: "Automação de Processos",
                  description: "Transforme processos manuais com automação inteligente e workflows otimizados para sua empresa.",
                  href: "/automacao-processos",
                  category: "Automação"
                }
              ]}
            />
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Services;